import { useCallback, useEffect, useState } from 'react'
import { fetchHealth, fetchInsight, type HealthSummary, type Insight, type MetricSummary } from '../lib/api'
import { ErrorBanner, Group, LoadError, Row, Screen, Spinner } from '../components/ui'
import { Sparkline, SparklineLegend } from '../components/sparkline'
import { IconFlask } from '../components/icons'

function formatValue(value: number | null, digits: number): string {
  if (value === null) return '—'
  return value.toLocaleString('ru-RU', { minimumFractionDigits: digits, maximumFractionDigits: digits })
}

/**
 * Значение с единицей. Единица есть не у всех метрик — у шагов и этажей её
 * нет вовсе, и подставлять её вслепую нельзя: на экране выходило «1 null».
 */
function withUnit(value: number | null, item: MetricSummary): string {
  return [formatValue(value, item.digits), item.unit].filter(Boolean).join(' ')
}

function formatDay(day: string | null): string {
  if (!day) return ''
  return new Date(`${day}T00:00:00`).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' })
}

/**
 * Насколько неделя отличается от предыдущей. Знак показываем, а «хорошо» или
 * «плохо» — нет: у пульса покоя вниз это хорошо, у вариабельности наоборот,
 * и приложение не должно решать это за человека.
 */
function formatTrend(item: MetricSummary): string | null {
  if (item.week_average === null || item.prev_week_average === null) return null
  if (item.prev_week_average === 0) return null

  const change = Math.round(((item.week_average - item.prev_week_average) / item.prev_week_average) * 100)
  if (change === 0) return 'как неделей раньше'
  return `${change > 0 ? '↑' : '↓'} ${Math.abs(change)}% к прошлой неделе`
}

export function HealthScreen({ onOpenLabs }: { onOpenLabs: () => void }) {
  const [health, setHealth] = useState<HealthSummary | null>(null)
  const [insight, setInsight] = useState<Insight | null>(null)
  const [error, setError] = useState<string | null>(null)

  const reload = useCallback(async () => {
    try {
      const [healthData, insightData] = await Promise.all([fetchHealth(), fetchInsight()])
      setHealth(healthData)
      setInsight(insightData.insight)
      setError(null)
    } catch (cause) {
      setError((cause as Error).message)
    }
  }, [])

  useEffect(() => {
    void reload()
  }, [reload])

  if (error && !health) return <LoadError message={error} onRetry={() => void reload()} />
  if (!health) return <Spinner label="Загружаю данные часов…" />
  if (!health.connected) return <NotConnected onOpenLabs={onOpenLabs} />

  // Наверх — то, что смотрят каждый день; остальное списком ниже.
  const primary = health.summary.filter((item) => item.primary)
  const secondary = health.summary.filter((item) => !item.primary && item.latest !== null)

  return (
    <Screen title="Здоровье">
      <LabsLink onOpenLabs={onOpenLabs} />

      {error && <ErrorBanner message={error} onClose={() => setError(null)} />}

      {health.stale && (
        <div className="rounded-card bg-sys-orange/15 px-4 py-3 text-[15px] text-sys-orange">
          Часы молчат больше суток. Открой Health Auto Export на телефоне — iOS не отдаёт данные,
          пока экран заблокирован.
        </div>
      )}

      <div className="grid grid-cols-2 gap-2">
        {primary.map((item) => (
          <MetricCard key={item.metric} item={item} stepsTarget={health.steps_target} />
        ))}
      </div>

      {secondary.length > 0 && (
        <Group caption="Ещё с часов" footnote="Среднее — по дням за последнюю неделю.">
          {secondary.map((item) => (
            <Row
              key={item.metric}
              title={item.label}
              subtitle={
                formatTrend(item) ??
                (item.week_average !== null
                  ? `в среднем ${withUnit(item.week_average, item)}`
                  : undefined)
              }
              value={withUnit(item.today ?? item.latest, item)}
            />
          ))}
        </Group>
      )}

      {health.workouts.length > 0 && (
        <Group caption="Тренировки с часов">
          {health.workouts.map((workout) => (
            <Row
              key={workout.id}
              title={workout.activity}
              value={[
                workout.distance_km ? `${workout.distance_km} км` : null,
                workout.duration_min ? `${workout.duration_min} мин` : null,
                workout.avg_heart_rate ? `пульс ${workout.avg_heart_rate}` : null,
              ]
                .filter(Boolean)
                .join(' · ')}
            />
          ))}
        </Group>
      )}

      {insight ? (
        <Group caption={`Отчёт за неделю · ${insight.period_start}`}>
          <div className="whitespace-pre-wrap px-4 py-3.5 text-[15px] leading-relaxed">
            {insight.content_md}
          </div>
        </Group>
      ) : (
        <Group caption="Недельный отчёт">
          <div className="footnote px-4 py-3.5">
            Первый отчёт придёт в воскресенье вечером — когда наберётся неделя данных.
          </div>
        </Group>
      )}
    </Screen>
  )
}

/**
 * Карточка метрики: крупное число за сегодня, неделя столбиками и одна строка
 * смысла под ней. Три уровня — «сколько сейчас», «как шло», «куда двигается».
 */
function MetricCard({ item, stepsTarget }: { item: MetricSummary; stepsTarget: number }) {
  const value = item.today ?? item.latest
  const hasToday = item.today !== null
  const trend = formatTrend(item)

  const caption = !hasToday && item.latest_day
    ? `за ${formatDay(item.latest_day)}`
    : item.metric === 'steps'
      ? `цель ${stepsTarget.toLocaleString('ru-RU')}`
      : item.week_average !== null
        ? `в среднем ${formatValue(item.week_average, item.digits)}`
        : 'первый день'

  return (
    <div className="rounded-card bg-ink-surface px-3.5 py-3">
      <div className="footnote">{item.label}</div>
      <div className="numerals mt-0.5 text-[28px] font-semibold leading-tight">
        {formatValue(value, item.digits)}
        {item.unit && <span className="ml-1 text-[15px] font-normal text-ink-2">{item.unit}</span>}
      </div>

      <div className="mt-2.5">
        <Sparkline days={item.days} tint={item.tint} />
        <SparklineLegend days={item.days} />
      </div>

      <div className="footnote mt-1.5 leading-tight">{trend ?? caption}</div>
    </div>
  )
}

function LabsLink({ onOpenLabs }: { onOpenLabs: () => void }) {
  return (
    <button
      type="button"
      onClick={onOpenLabs}
      className="flex min-h-14 items-center gap-3 rounded-card bg-ink-surface px-4 text-left active:opacity-70"
    >
      <IconFlask size={22} className="text-sys-purple" />
      <span className="min-w-0 flex-1">
        <span className="block text-[17px]">Анализы и InBody</span>
        <span className="footnote block">Загрузить бланк, посмотреть динамику</span>
      </span>
      <span aria-hidden="true" className="text-ink-3">
        →
      </span>
    </button>
  )
}

function NotConnected({ onOpenLabs }: { onOpenLabs: () => void }) {
  return (
    <Screen title="Здоровье">
      <LabsLink onOpenLabs={onOpenLabs} />

      <Group caption="Часы не подключены" footnote="После первого синка вкладка заполнится сама.">
        <div className="px-4 py-3.5 text-[15px] leading-relaxed text-ink-2">
          У Apple Health нет облачного API — данные живут на телефоне. Нужен мост: приложение
          Health Auto Export с автоматизацией REST API. Адрес и ключ спроси у меня в чате.
        </div>
      </Group>
    </Screen>
  )
}
